import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Autosave for a rater's judgement in progress — shared by both RLHF tracks.
 *
 * The 1-5 scoring page and the side-by-side comparison page have the same shape of problem:
 * a choice plus written reasoning, drafted as the rater types and flushed before navigating
 * away. Only the choice's type and the endpoint differ, so this hook is generic over both and
 * each page wraps it.
 *
 * Losing a rater's written reasoning is the worst outcome either feature has — it is
 * hand-authored research data that cannot be regenerated — which is exactly why this logic
 * lives in ONE place. A second copy would be a second chance to reintroduce the bug below.
 *
 * The critical rule: autosave fires ONLY on a genuine edit. An earlier version armed a draft
 * save on every effect run, so merely *opening* an item scheduled a write — and when the form
 * had been hydrated from a stale queue entry, that write silently overwrote good remarks with
 * an empty string and reported "Saved". Everything here is built around comparing against a
 * baseline rather than assuming a change occurred.
 */

export const AUTOSAVE_DEBOUNCE_MS = 800;

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export interface JudgementPayload<TChoice> {
  choice: TChoice;
  remarks: string;
  draft: boolean;
}

interface UseJudgementAutosaveArgs<TChoice> {
  /** The item being judged (an assignment id, or a pair id). Null when nothing is loaded. */
  itemId: number | null;
  /** The rater's choice — a 1-5 score, or which report won. */
  choice: TChoice;
  remarks: string;
  /** Suppresses drafting while the form is being populated from the server. */
  enabled: boolean;
  /** Persists to the track's own endpoint. */
  save: (itemId: number, payload: JudgementPayload<TChoice>) => Promise<unknown>;
  /** Shown when the save fails, phrased for the track. */
  failureMessage: string;
  /**
   * Called after EVERY successful save, draft included, with the values actually committed.
   *
   * This exists because the page keeps its own copy of each item in a queue, and that queue is
   * what the hydration effect restores from when the rater navigates back. Without mirroring
   * draft saves into it, the queue silently becomes stale: the server holds the reasoning, the
   * queue still holds `null`, and revisiting the item shows an empty box that the next keystroke
   * writes back over the good text. Submit-only mirroring is not enough — most of a rater's
   * typing is persisted by drafts.
   */
  onSaved?: (committed: { itemId: number; choice: TChoice; remarks: string; draft: boolean }) => void;
}

export function useJudgementAutosave<TChoice>({
  itemId, choice, remarks, enabled, save, failureMessage, onSaved,
}: UseJudgementAutosaveArgs<TChoice>) {
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Monotonic ticket per save. Two writes for the same item can be in flight at once — the
  // debounce fires a draft, then the rater immediately hits Next, which submits — and they can
  // resolve out of order. Only the newest may commit the baseline and report upward; otherwise a
  // late-landing OLDER draft would overwrite the page's queue entry with stale text, and the
  // queue is what the form rehydrates from.
  const saveSeqRef = useRef(0);

  // The last values known to match the server, per item. A save is only worth making (and only
  // counts as "dirty") when the current values differ from this.
  const baselineRef = useRef<{ id: number | null; choice: TChoice | null; remarks: string }>({
    id: null, choice: null, remarks: '',
  });

  // Latest values for `flush`, written in an effect rather than during render so a discarded or
  // double-invoked render can never leave uncommitted values behind.
  const latest = useRef({ itemId, choice, remarks });
  useEffect(() => {
    latest.current = { itemId, choice, remarks };
  }, [itemId, choice, remarks]);

  // `save` is typically an inline arrow, so it changes identity every render. Held in a ref so
  // it never re-arms the debounce timer.
  const saveRef = useRef(save);
  useEffect(() => { saveRef.current = save; }, [save]);

  // Held in a ref for the same reason as `save`: the page passes a closure over its queue state,
  // so it changes identity constantly and must never re-arm the debounce timer.
  const onSavedRef = useRef(onSaved);
  useEffect(() => { onSavedRef.current = onSaved; }, [onSaved]);

  const isDirty = useCallback(() => {
    const b = baselineRef.current;
    if (b.id !== itemId) return false; // baseline not established for this item yet
    return b.choice !== choice || b.remarks !== remarks;
  }, [itemId, choice, remarks]);

  /**
   * Adopt the given values as the server truth. Called on hydration so that simply opening an
   * item is never mistaken for an edit.
   */
  const setBaseline = useCallback((id: number | null, nextChoice: TChoice, text: string) => {
    baselineRef.current = { id, choice: nextChoice, remarks: text };
    setSaveState('idle');
    setError(null);
  }, []);

  /** Persist immediately. `draft: false` marks the item judged. Returns whether it succeeded. */
  const flush = useCallback(async (draft: boolean) => {
    const { itemId: id, choice: c, remarks: text } = latest.current;
    if (id === null) return true;

    const b = baselineRef.current;
    const unchanged = b.id === id && b.choice === c && b.remarks === text;
    // A draft with nothing new is a no-op. A real submit still goes through even when the values
    // are unchanged, because it also flips the item to 'submitted'.
    if (draft && unchanged) return true;

    if (timerRef.current) clearTimeout(timerRef.current);
    const ticket = ++saveSeqRef.current;
    setSaveState('saving');
    setError(null);
    try {
      await saveRef.current(id, { choice: c, remarks: text, draft });
    } catch (e) {
      if (ticket === saveSeqRef.current) {
        setSaveState('error');
        setError(e instanceof Error ? e.message : failureMessage);
      }
      return false;
    }
    // Superseded by a newer write that already landed — its values are the current truth, so
    // committing OUR values would move the baseline and the queue backwards.
    if (ticket !== saveSeqRef.current) {
      // But a real submit (draft:false) flipped the item to 'submitted' server-side, and that
      // status is what marks the queue entry judged. Skipping onSaved here left the queue
      // is_rated=false after a successful submit. Report the judged status — with the LATEST
      // values for this item (not our stale ones), so the mirror stays forward, never back.
      if (!draft) {
        const cur = latest.current;
        const fresh = cur.itemId === id ? { choice: cur.choice, remarks: cur.remarks } : { choice: c, remarks: text };
        try {
          onSavedRef.current?.({ itemId: id, choice: fresh.choice, remarks: fresh.remarks, draft: false });
        } catch (e) {
          console.error('Autosave committed, but the caller failed to record it', e);
        }
      }
      return true;
    }

    baselineRef.current = { id, choice: c, remarks: text };
    setSaveState('saved');
    // Reported outside the save's try/catch and guarded separately: this is the caller's own
    // bookkeeping. The server has already accepted the write, so a throw here must neither be
    // shown as "your rating failed to save" nor block the navigation that follows.
    try {
      onSavedRef.current?.({ itemId: id, choice: c, remarks: text, draft });
    } catch (e) {
      console.error('Autosave committed, but the caller failed to record it', e);
    }
    return true;
  }, [failureMessage]);

  /**
   * Drop any pending draft save.
   *
   * Needed when the page abandons an item — skipping a pair tells the rater their work is
   * discarded, so an armed timer must not fire afterwards and write it back anyway.
   */
  const cancelPending = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    saveSeqRef.current += 1;   // also disarms any in-flight write
  }, []);

  // Debounced draft save — armed only by a real edit.
  useEffect(() => {
    if (!enabled || itemId === null) return;
    if (baselineRef.current.id !== itemId) return;
    if (!isDirty()) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { void flush(true); }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [itemId, choice, remarks, enabled, isDirty, flush]);

  // A reload or tab close mid-sentence must not silently discard the reasoning.
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (isDirty()) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  return { saveState, error, flush, setBaseline, isDirty, cancelPending };
}
